// src/pages/lab/protocols/ProtocolManagementPage.tsx
import React, { useState, useEffect } from "react";
import { Button, Modal, Space, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, EditOutlined, CopyOutlined, PlayCircleOutlined, PauseCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import PageContainer from "../../../../components/shared/PageContainer";
import PageToolbar from "../../../../components/shared/PageToolbar";
import { DataTable, StatusTag } from "../../../../components/shared";
import { useNotifications } from "../../../../context/NotificationContext";

import { fetchProtocols, deleteProtocol, activateProtocol, deactivateProtocol, cloneProtocol } from "../../../../api/labiq/ProtocalApi";
import { Protocol, ProtocolFilter } from "../../../../types/lab/ProtocolTypes";
import { Company } from "../../../../types/admin/CompanyTypes";
import { fetchClinicCompany } from "../../../../api/admin/companyApi";
import { sampleTypeOptions } from "../../../../types/lab/SampleTypes";
import ProtocolFlowBuilderModal from "./ProtocolFlowBuilderModal";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;
const { confirm } = Modal;

const ITEMS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

const ProtocolManagementPage: React.FC = () => {
	const [protocols, setProtocols] = useState<Protocol[]>([]);
	const [totalProtocols, setTotalProtocols] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [company, setCompany] = useState<Company | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingProtocol, setEditingProtocol] = useState<Protocol | null>(null);

	const [filters, setFilters] = useState<ProtocolFilter>({
		active_only: undefined,
		test_type: "",
		sample_type: "",
		search: "",
		sort_by: "name",
		sort_order: "asc",
		per_page: ITEMS_PER_PAGE,
		page: 1,
	});

	const { showSuccess, showError } = useNotifications();



	const loadProtocols = async () => {
		setLoading(true);
		try {
			const response = await fetchProtocols({
				...filters,
				page: currentPage,
				per_page: ITEMS_PER_PAGE,
			});
			setProtocols(response.data);
			setTotalProtocols(response.total);
		} catch (error) {
			showError("Failed to load protocols");
			console.error("Error loading protocols:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (company) {
			loadProtocols();
		}
	}, [filters, currentPage, company]);

	useEffect(() => {
		const loadCompany = async () => {
			const comp = await fetchClinicCompany();
			setCompany(comp);
		};
		if (!company) {
			loadCompany();
		}
	}, []);

	const handleOpenModal = (protocol?: Protocol) => {
		setEditingProtocol(protocol || null);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setEditingProtocol(null);
	};

	const handleClone = async (protocol: Protocol) => {
		const newVersion = prompt(`Clone protocol "${protocol.name}" with version:`, `${protocol.version}.1`);
		if (!newVersion) return;
		try {
			await cloneProtocol(protocol.id, { new_version: newVersion });
			showSuccess(`Protocol cloned successfully as version ${newVersion}`);
			loadProtocols();
		} catch (error) {
			showError("Failed to clone protocol");
		}
	};

	const handleDelete = (protocol: Protocol) => {
		confirm({
			title: "Confirm Deletion",
			content: `Are you sure you want to delete protocol "${protocol.name}"? This action cannot be undone.`,
			okText: "Delete",
			okType: "danger",
			onOk: async () => {
				try {
					await deleteProtocol(protocol.id);
					showSuccess("Protocol deleted successfully");
					loadProtocols();
				} catch (error) {
					showError("Failed to delete protocol");
				}
			},
		});
	};

	const handleToggleActive = async (protocol: Protocol) => {
		try {
			if (protocol.is_active) {
				await deactivateProtocol(protocol.id);
				showSuccess(`Protocol "${protocol.name}" deactivated`);
			} else {
				await activateProtocol(protocol.id);
				showSuccess(`Protocol "${protocol.name}" activated`);
			}
			loadProtocols();
		} catch (error) {
			showError(`Failed to ${protocol.is_active ? "deactivate" : "activate"} protocol`);
		}
	};

	const handleFilterChange = (field: keyof ProtocolFilter, value: any) => {
		setFilters((prev) => ({ ...prev, [field]: value }));
		setCurrentPage(1);
	};

	const resetFilters = () => {
		setFilters({
			active_only: undefined,
			test_type: "",
			sample_type: "",
			search: "",
			sort_by: "name",
			sort_order: "asc",
			per_page: 25,
			page: 1,
		});
		setCurrentPage(1);
	};

	const activeFilterOptions = [
		{ label: "All Protocols", value: "" },
		{ label: "Active Only", value: "true" },
		{ label: "Inactive Only", value: "false" },
	];

	const columns: ColumnsType<Protocol> = [
		{
			title: "Protocol Name",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (text: string, record: Protocol) => (
				<div>
					<Text strong>{text}</Text>
					<Text type="secondary" style={{ display: "block", fontSize: 12 }}>v{record.version}</Text>
				</div>
			),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			ellipsis: true,
			render: (text: string) => (
				<Text type="secondary">{text || "—"}</Text>
			),
		},
		{
			title: "Status",
			dataIndex: "is_active",
			key: "status",
			render: (isActive: boolean) => (
				<StatusTag status={isActive ? "active" : "inactive"} />
			),
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
			render: (date: string) => (
				<Text type="secondary">{dayjs(date).format("D MMM YYYY")}</Text>
			),
		},
		{
			title: "",
			key: "actions",
			fixed: "right" as const,
			render: (record: Protocol) => (
				<Space size={2}>
					<Tooltip title="Edit">
						<Button
							type="text"
							size="small"
							icon={<EditOutlined />}
							onClick={(e) => { e.stopPropagation(); handleOpenModal(record); }}
						/>
					</Tooltip>
					<Tooltip title="Clone">
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined />}
							onClick={(e) => { e.stopPropagation(); handleClone(record); }}
						/>
					</Tooltip>
					<Tooltip title={record.is_active ? "Deactivate" : "Activate"}>
						<Button
							type="text"
							size="small"
							icon={record.is_active ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
							onClick={(e) => { e.stopPropagation(); handleToggleActive(record); }}
						/>
					</Tooltip>
					<Tooltip title="Delete">
						<Button
							type="text"
							size="small"
							danger
							icon={<DeleteOutlined />}
							onClick={(e) => { e.stopPropagation(); handleDelete(record); }}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	return (
		<PageContainer>
			<PageToolbar
				primaryAction={{
					label: "New Protocol",
					icon: <PlusOutlined />,
					onClick: handleOpenModal,
				}}
				filters={[
					{
						type: "search",
						placeholder: "Search protocols…",
						value: filters.search || "",
						onChange: (v) => handleFilterChange("search", v),
						width: 220,
					},
					{
						type: "select",
						placeholder: "All statuses",
						options: activeFilterOptions,
						value: filters.active_only === undefined ? "" : String(filters.active_only),
						onChange: (v) => handleFilterChange("active_only", v === "" ? undefined : v === "true"),
						width: 150,
					},
					{
						type: "select",
						placeholder: "All sample types",
						options: sampleTypeOptions,
						value: filters.sample_type || "",
						onChange: (v) => handleFilterChange("sample_type", v),
						width: 160,
					},
				]}
				onReset={resetFilters}
			/>
			<DataTable
				columns={columns}
				dataSource={protocols}
				rowKey="id"
				loading={loading}
				onRowClick={(record) => handleOpenModal(record)}
				total={totalProtocols}
				currentPage={currentPage}
				pageSize={ITEMS_PER_PAGE}
				onPageChange={setCurrentPage}
				emptyTitle="No protocols found"
				emptyDescription="Create your first laboratory protocol to get started"
				emptyAction={{ label: "New Protocol", onClick: handleOpenModal }}
				scrollX={1200}
			/>

			{isModalOpen && (
				<ProtocolFlowBuilderModal
					visible={isModalOpen}
					onClose={handleCloseModal}
					protocol={editingProtocol}
				/>
			)}
		</PageContainer>
	);
};

export default ProtocolManagementPage;

